import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import {
  Field,
  FieldHelp,
  FieldType,
  MemberAddeditComponent,
  MemberAddeditDialogModel,
} from 'src/app/components/brokeragency/member-addedit/member-addedit.component';
import { UtilsService } from 'src/app/services/utils/utils.service';
import {
  CampaignData,
  CampaignInterface,
  CampaignTypeStatus,
  MarketingArchiveStatusFilter,
  MarketingCampaignChannel,
  PostCampaignInterface,
} from '../../../../interfaces/campaigns/campaign.interface';
import { GroupInterface } from '../../../../interfaces/groups/group.interface';
import { GroupsService } from '../../../../services/groups/groups.service';
import { MarketingCampaignsService } from '../../../../services/marketing-campaigns/marketing-campaigns.service';
import { UiAlertService } from '../../../../services/ui-alert/ui-alert.service';

interface CampaignRunFailure {
  campaignId: string;
  to: string;
  message: string;
  statusCode?: number;
  errors?: unknown;
}

interface CampaignRunError {
  data?: {
    message?: string;
    failures?: CampaignRunFailure[];
  };
  message?: string;
}

interface CampaignTargetCountResponse {
  count: number;
  channel?: MarketingCampaignChannel | string;
  skipped?: Array<{
    target: string;
    reason: string;
  }>;
}

@Component({
  selector: 'app-marketing-campaigns-list',
  templateUrl: './marketing-campaigns-list.component.html',
  styleUrls: ['./marketing-campaigns-list.component.scss']
})
export class MarketingCampaignsListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  columnNames = ['name', 'channel', 'status', 'groupNames', 'createdAt', 'actionmenu'];
  tableDataSource = new MatTableDataSource<any>();
  private readonly runBlockedStatuses = ['queued', 'running', 'processing', 'sending'];
  private readonly channelOptions: Array<{ label: string; value: MarketingCampaignChannel }> = [
    { label: 'Email', value: 'email' },
    { label: 'SMS', value: 'sms' },
  ];
  private readonly campaignHandlebarsHelp: FieldHelp = {
    title: 'Handlebars fields',
    description: 'Email campaign content can use Handlebars variables, such as {{user.firstName}}.',
    groups: [
      {
        title: 'User',
        fields: [
          '{{user.id}}',
          '{{user.userId}}',
          '{{user.firstName}}',
          '{{user.lastName}}',
          '{{user.name}}',
          '{{user.nickname}}',
          '{{user.email}}',
          '{{user.birthdate}}',
          '{{user.address1}}',
          '{{user.address2}}',
          '{{user.city}}',
          '{{user.state}}',
          '{{user.country}}',
          '{{user.postalCode}}',
          '{{user.gender}}',
          '{{user.avatar}}',
          '{{user.createdAt}}',
          '{{user.modifiedAt}}',
        ],
      },
      {
        title: 'Team',
        fields: [
          '{{team.id}}',
          '{{team.teamId}}',
          '{{team.name}}',
          '{{team.frequency}}',
          '{{team.amount}}',
          '{{team.ownerUserId}}',
          '{{team.disabled}}',
          '{{team.approved}}',
          '{{team.plan}}',
          '{{team.topupDisabled}}',
          '{{team.invoiceEmail}}',
          '{{team.companyId}}',
          '{{team.createdAt}}',
          '{{team.metadata}}',
        ],
      },
      {
        title: 'Brand',
        fields: [
          '{{brand.id}}',
          '{{brand.partnerId}}',
          '{{brand.name}}',
          '{{brand.shortName}}',
          '{{brand.bankPartnerId}}',
          '{{brand.tagline}}',
          '{{brand.logoUrl}}',
          '{{brand.lightLogoUrl}}',
          '{{brand.horizontalLogoUrl}}',
          '{{brand.redirectUrl}}',
          '{{brand.configjson}}',
          '{{brand.extraData}}',
        ],
      },
      {
        title: 'Campaign',
        fields: [
          '{{campaign.id}}',
          '{{campaign.name}}',
        ],
      },
      {
        title: 'Sequence',
        fields: [
          '{{sequence.id}}',
          '{{sequence.name}}',
        ],
      },
      {
        title: 'Recipient',
        fields: [
          '{{recipient.email}}',
          '{{recipient.name}}',
          '{{recipient.userId}}',
          '{{recipient.enrollmentId}}',
          '{{recipient.sequenceId}}',
          '{{recipient.sourceImportId}}',
          '{{recipient.phoneNumber}}',
        ],
      },
    ],
  };

  groups: GroupInterface[] = [];
  campaignCount = 0;
  campaignArchiveStatusFilter: MarketingArchiveStatusFilter = 'active';
  archiveStatusOptions: Array<{ label: string; value: MarketingArchiveStatusFilter }> = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Archived', value: 'archived' },
  ];

  constructor(
    private marketingCampaignsService: MarketingCampaignsService,
    private groupsService: GroupsService,
    private uiAlertService: UiAlertService,
    private snackBar: MatSnackBar,
    public utilsService: UtilsService,
    private dialog: MatDialog,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    this.load();
  }

  ngAfterViewInit(): void {
    this.tableDataSource.sort = this.sort;
  }

  async play(campaign: CampaignInterface): Promise<void> {
    if (!this.hasTargetGroups(campaign)) {
      this.snackBar.open('Select at least one target group before running this campaign.', '', { duration: 5000 });
      return;
    }

    const countResponse = await this.marketingCampaignsService.getTargetCount(campaign.id) as CampaignTargetCountResponse;
    const { count } = countResponse;
    if (count === 0) {
      this.snackBar.open(this.getNoTargetsMessage(countResponse), '', { duration: 5000 });
      return;
    }
    const actionLabel = this.getRunActionLabel(campaign);
    const ok = await this.uiAlertService.presentAlertConfirm(
      `Do you really want to ${actionLabel.toLowerCase()} this campaign? It will be sent to ${count} target${count === 1 ? '' : 's'}. 
      This action cannot be undone. Do you want to continue?`
    );
    if (ok) {
      try {
        await this.marketingCampaignsService.runById(campaign.id);
        this.snackBar.open('Campaign started successfully.', '', { duration: 2000 });
      } catch (error) {
        this.snackBar.open(this.getCampaignRunErrorMessage(error), '', { duration: 5000 });
      } finally {
        this.load();
      }
    }
  }

  load(): void {
    this.marketingCampaignsService.getAll(this.campaignArchiveStatusFilter).then(campaigns => {
      this.campaignCount = campaigns.length;
      this.tableDataSource.data = campaigns.map(
        (c) => {
          return {
            ...c,
            groupNames: (c.groups || []).map(g => g.name).join(', ')
          }
        }
      );
    });
    this.groupsService.getAll().then(groups => {
      this.groups = groups;
    });
  }

  getStatusCount(status: string): number {
    return this.tableDataSource.data.filter(campaign => campaign.status === status).length;
  }

  onArchiveStatusFilterChange(): void {
    this.load();
  }

  openGroups(): void {
    this.router.navigate(['/marketing-campaigns/groups']).then();
  }

  viewGroup(group: { id: string }): void {
    this.router.navigate(
      ['/marketing-campaigns/groups'],
      { queryParams: { groupId: group.id } },
    ).then();
  }

  getStatusClass(status: string): string {
    return `status-${status || 'default'}`;
  }

  getStatusLabel(status: string): string {
    return status === 'created' ? 'Not Run' : this.utilsService.capitalize(status);
  }

  getChannelLabel(channel?: MarketingCampaignChannel | string): string {
    return this.channelOptions.find(option => option.value === channel)?.label || 'Email';
  }

  canRunCampaign(campaign: CampaignInterface): boolean {
    return !this.isArchived(campaign) && !this.runBlockedStatuses.includes(campaign.status);
  }

  getRunActionLabel(campaign: CampaignInterface): string {
    return campaign.status === 'created' ? 'Run' : 'Run Again';
  }

  isArchived(campaign: CampaignInterface): boolean {
    return campaign.status === 'archived';
  }

  add(): void {
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        { channel: 'email', groupIds: [] },
        this.getCampaignDialogFields(false, true),
        'Marketing Campaign',
        true,
        'Add',
      ),
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }

      try {
        await this.marketingCampaignsService.create(this.toCampaignPayload(result));
        this.snackBar.open('Campaign created.', '', { duration: 2000 });
        this.load();
      } catch (error) {
        console.error(error);
        this.snackBar.open('Error while creating a Campaign. Try again in a few minutes.', '', {
          duration: 2000,
        });
      }
    });
  }

  edit(campaign: CampaignInterface): void {
    const canEditCampaignDetails = this.canEditCampaignDetails(campaign);
    const dialogRef = this.dialog.open(MemberAddeditComponent, {
      data: new MemberAddeditDialogModel(
        this.toCampaignDialogData(campaign),
        this.getCampaignDialogFields(!canEditCampaignDetails, true, false),
        'Marketing Campaign',
        true,
        'Edit',
        true,
      ),
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (!result) {
        return;
      }

      try {
        if (canEditCampaignDetails) {
          await this.marketingCampaignsService.updateById(
            campaign.id,
            this.toCampaignPayload(result),
          );
        } else {
          await this.marketingCampaignsService.renameById(campaign.id, result.name);
        }
        this.snackBar.open('Campaign updated.', '', { duration: 2000 });
        this.load();
      } catch (error) {
        console.error(error);
        this.snackBar.open('Error while updating a Campaign. Try again in a few minutes.', '', {
          duration: 2000,
        });
      }
    });
  }

  async archive(campaign: CampaignInterface): Promise<void> {
    const ok = await this.uiAlertService.presentAlertConfirm(
      'Do you really want to archive this campaign?',
    );
    if (!ok) {
      return;
    }

    try {
      await this.marketingCampaignsService.archiveById(campaign.id);
      this.snackBar.open('Campaign archived.', '', { duration: 2000 });
      this.load();
    } catch (error) {
      console.error(error);
      this.snackBar.open('Error while archiving this Campaign. Try again in a few minutes.', '', {
        duration: 2000,
      });
    }
  }

  async clone(item: any): Promise<void> {
    const ok = await this.uiAlertService.presentAlertConfirm(
      'Do you really want to clone this campaign?');
    if (ok) {
      try {
        await this.marketingCampaignsService.cloneById(item.id);
        this.snackBar.open('Campaign duplicated successfully.', '', { duration: 2000 });
      } catch (error) {
        console.error(error);
        this.snackBar.open(
          'Error while duplicated this Campaign. Try again in a few minutes.',
          '',
          { duration: 2000 }
        );
      } finally {
        this.load();
      }
    }
  }

  private getCampaignDialogFields(readOnly: boolean, subjectRequired: boolean, nameReadOnly = readOnly): Field[] {
    return [
      {
        name: 'name',
        label: 'Name',
        fieldType: FieldType.F_STRING,
        required: true,
        readOnly: nameReadOnly,
        sectionTitle: 'Campaign Setup',
      },
      {
        name: 'channel',
        label: 'Channel',
        fieldType: FieldType.F_DROPDOWN,
        values: this.channelOptions.map(option => `${option.label}:${option.value}`),
        required: true,
        readOnly,
      },
      {
        name: 'description',
        label: 'Description',
        fieldType: FieldType.F_STRING,
        required: true,
        readOnly,
      },
      {
        name: 'subject',
        label: 'Subject',
        fieldType: FieldType.F_STRING,
        required: subjectRequired,
        readOnly,
        invisible: form => form.get('channel')?.value === 'sms',
        hint: 'Handlebars variables can be used here.',
        help: this.campaignHandlebarsHelp,
      },
      { name: 'title', label: 'Title', fieldType: FieldType.F_STRING, readOnly, invisible: form => form.get('channel')?.value === 'sms' },
      {
        name: 'header',
        label: 'Header',
        fieldType: FieldType.F_STRING,
        readOnly,
        invisible: form => form.get('channel')?.value === 'sms',
        hint: 'Handlebars variables can be used here.',
        help: this.campaignHandlebarsHelp,
      },
      {
        name: 'body',
        label: 'Body',
        labelResolver: form => form.get('channel')?.value === 'sms' ? 'SMS Message' : 'Body',
        fieldType: FieldType.F_STRING,
        required: true,
        readOnly,
        sectionTitle: 'Content',
        hint: 'For email campaigns, Handlebars variables can be used here.',
        help: this.campaignHandlebarsHelp,
      },
      {
        name: 'footer',
        label: 'Footer',
        fieldType: FieldType.F_STRING,
        readOnly,
        invisible: form => form.get('channel')?.value === 'sms',
        hint: 'Handlebars variables can be used here.',
        help: this.campaignHandlebarsHelp,
      },
      {
        name: 'groupIds',
        label: 'Target Groups',
        fieldType: FieldType.F_DROPDOWN,
        values: this.groups.map(group => `${group.name}:${group.id}`),
        searchable: true,
        searchPlaceholder: 'Search groups...',
        multiple: true,
        required: false,
        readOnly,
        sectionTitle: 'Targeting',
      },
    ];
  }

  private canEditCampaignDetails(campaign: CampaignInterface): boolean {
    return campaign.status === CampaignTypeStatus.Created || campaign.status === CampaignTypeStatus.Completed;
  }

  private toCampaignDialogData(campaign: CampaignInterface): Record<string, any> {
    return {
      name: campaign.name,
      description: campaign.description,
      channel: campaign.channel || 'email',
      subject: campaign.data?.subject || '',
      title: campaign.data?.title || '',
      header: campaign.data?.header || '',
      body: campaign.data?.body || '',
      footer: campaign.data?.footer || '',
      groupIds: (campaign.groups || []).map(group => group.id),
    };
  }

  private toCampaignPayload(result: Record<string, any>): PostCampaignInterface {
    return {
      name: result.name,
      description: result.description,
      channel: result.channel || 'email',
      groupIds: result.groupIds || [],
      data: this.toCampaignData(result),
    };
  }

  private toCampaignData(result: Record<string, any>): CampaignData {
    return {
      subject: result.subject || '',
      title: result.title || '',
      header: result.header || '',
      body: result.body || '',
      footer: result.footer || '',
    };
  }

  private hasTargetGroups(campaign: CampaignInterface): boolean {
    return !!campaign.groups?.length;
  }

  private getCampaignRunErrorMessage(error: unknown): string {
    const campaignRunError = error as CampaignRunError;
    const failures = campaignRunError?.data?.failures || [];
    const responseMessage = campaignRunError?.data?.message;

    if (failures.length === 1) {
      const failure = failures[0];
      const failureMessage = failure.message || responseMessage || 'Unknown send error';

      return `Failed to send 1 email: ${failure.to} - ${failureMessage}`;
    }

    if (failures.length > 1) {
      return `Failed to send ${failures.length} emails. See History for details.`;
    }

    return responseMessage
      || campaignRunError?.message
      || 'Error while starting this Campaign. Try again in a few minutes.';
  }

  private getNoTargetsMessage(countResponse: CampaignTargetCountResponse): string {
    if (countResponse.channel === 'sms' && countResponse.skipped?.length) {
      const firstSkipped = countResponse.skipped[0];
      return `${firstSkipped.target} does not have a confirmed SMS-enabled phone number.`;
    }

    if (countResponse.channel === 'sms') {
      return 'No SMS targets with confirmed SMS-enabled phone numbers.';
    }

    return 'No targets to send this campaign.';
  }
}
